import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { ChecklistWrapper, ChecklistHeader, ChecklistBody } from '../../NodeContentPreview/styles';
import {
  ContentSection,
  ChecklistTasks,
  ChecklistTaskAdd,
  AddButton,
  ChecklistTask,
  ChecklistTaskItem,
  ChecklistTaskOrder,
  ChecklistTaskContent,
  ChecklistTaskInput,
  ChecklistTaskTextInput,
  ChecklistTaskOptions,
  ChecklistTaskOptionItem,
} from '../styles';

const ChecklistContentEditor = ({ children, data, payload, setPayload, loading = false, updating = false }) => {
  const isUpdating = loading || updating;
  const useAutoSave = window.editor.useAutoSave;

  const mapItems = (items) => {
    return items.map((item) => ({
      ...item,
    }));
  };

  const [items, setItems] = useState(mapItems(data?.ChecklistItems) || []);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef();

  const setFocus = () => {
    if (inputRef?.current && !inputRef?.current?.value?.trim()) {
      inputRef.current.focus();
    }
  };

  useEffect(() => {
    setPayload({ ...payload, items });
  }, []);

  useEffect(() => {
    if (!isUpdating && JSON.stringify(items) !== JSON.stringify(data.ChecklistItems)) {
      setPayload({ ...payload, items, immediateSave: items.length !== data.ChecklistItems.length });
      setFocus();
    }
  }, [JSON.stringify(items)]);

  const getMissingId = (items) => {
    let result = -1;
    items.forEach((item, index) => {
      if (!item.id) {
        result = index;
      }
    });
    return result;
  };

  useEffect(() => {
    const index = getMissingId(items);
    const newItems = [...items];
    if (index !== -1 && newItems[index]) {
      newItems[index] = { ...data.ChecklistItems[index], order: index };
      setItems(newItems);
    }
  }, [JSON.stringify(data.ChecklistItems)]);

  const orderSorter = (a, b) => a.order - b.order;

  const addTask = () => {
    if (isUpdating) {
      return;
    }
    const newItems = [...items];
    newItems.push({ type: 'checklist_item', content: '', order: newItems.length });
    setItems(newItems);
    setSelectedIndex(items.length);
  };

  const removeItem = (order) => {
    if (isUpdating) {
      return;
    }
    const newItems = items.filter((item) => item.order !== order).map((item, index) => ({ ...item, order: index }));
    setItems(newItems);
  };

  const onChange = (value, index) => {
    const newItems = [...items];
    newItems[index].content = value;
    setItems(newItems);
  };

  const orderIsDisabled = (index, delta) =>
    loading || (index === 0 && delta === -1) || (index === items.length - 1 && delta === 1);

  const orderItem = (index, delta) => {
    if (isUpdating || orderIsDisabled(index, delta)) {
      return;
    }
    const newItems = [...items];
    newItems[index].order = index + delta;
    newItems[index + delta].order = index;
    setItems(newItems);
  };

  const editTask = (index) => {
    setSelectedIndex(index);
  };

  const handleKeyPress = (event) => {
    switch (event.key) {
      case 'Enter':
        if (selectedIndex === items.length - 1) {
          addTask();
        } else {
          setSelectedIndex(-1);
        }
        break;
      case 'Escape':
        setSelectedIndex(-1);
        break;
    }
  };

  const isDisabled = (id) => useAutoSave && !id;

  const list = items.sort(orderSorter).map((item, index) => {
    const { order, content, id } = item;
    const count = order + 1;
    return (
      <ChecklistTask
        key={index}
        selected={index === selectedIndex}
        title={i18n.__('checklists.edit_task')}
        onClick={() => editTask(index)}
      >
        {index !== selectedIndex ? (
          <ChecklistTaskItem>
            <ChecklistTaskOrder>{count}.</ChecklistTaskOrder>
            <ChecklistTaskContent>{content}</ChecklistTaskContent>
          </ChecklistTaskItem>
        ) : (
          <ChecklistTaskInput>
            <ChecklistTaskOrder>{count}.</ChecklistTaskOrder>
            <ChecklistTaskTextInput
              id={id}
              placeholder={i18n.__('checklists.placeholder')}
              value={content || ''}
              onChange={(ev) => !loading && onChange(ev.target.value, index)}
              onKeyUp={(ev) => handleKeyPress(ev, index)}
              onBlur={() => setSelectedIndex(-1)}
              autoFocus={!loading && !isDisabled(id)}
              disabled={loading || isDisabled(id)}
              ref={inputRef}
            />
          </ChecklistTaskInput>
        )}
        <ChecklistTaskOptions>
          <ChecklistTaskOptionItem
            className="fa fa-times-circle"
            disabled={loading}
            title={i18n.__('app_remove')}
            onClick={() => removeItem(index)}
          />
          <ChecklistTaskOptionItem
            className="fa fa-arrow-up"
            disabled={orderIsDisabled(index, -1)}
            title={i18n.__('app_move_up')}
            onClick={() => orderItem(index, -1)}
          />
          <ChecklistTaskOptionItem
            className="fa fa-arrow-down"
            disabled={orderIsDisabled(index, +1)}
            title={i18n.__('app_move_down')}
            onClick={() => orderItem(index, 1)}
          />
        </ChecklistTaskOptions>
      </ChecklistTask>
    );
  });

  return (
    <ContentSection>
      {children}
      <ChecklistWrapper>
        <ChecklistHeader>
          <h2>{i18n.__('checklists.header')}</h2>
        </ChecklistHeader>
        <ChecklistBody>
          <ChecklistTasks>{list}</ChecklistTasks>
          <ChecklistTaskAdd>
            <AddButton onClick={addTask} disabled={loading} title={i18n.__('checklists.add_task')}>
              {i18n.__('checklists.add_task')}
            </AddButton>
          </ChecklistTaskAdd>
        </ChecklistBody>
      </ChecklistWrapper>
    </ContentSection>
  );
};

export default ChecklistContentEditor;

ChecklistContentEditor.propTypes = {
  children: PropTypes.object.isRequired,
  data: PropTypes.object.isRequired,
  payload: PropTypes.object.isRequired,
  setPayload: PropTypes.func.isRequired,
  loading: PropTypes.bool,
  updating: PropTypes.bool,
};
